import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { useCompanyContext } from "Bands/contexts";
import { copy } from "shared/assets";
import { IActionDropdownItem, Pager, Table, TableColumn, TableSkeleton } from "shared/components";
import { formatDateTime, formatNumber } from "shared/utils";
import { Spacer } from "shared/layout";
import { dispatchGetCompanyInvoices, dataMetaSelector } from "Bands/state";
import { usePagination } from "Bands/utils";
import { BAND_ACTIONS } from "Bands/types";
import { IAPIState, ITheme } from "shared/types";

const invoicesCopy = copy.companyDashboard.invoices;

export const InvoicesTable = () => {
  const dispatch = useDispatch();
  const { api: apiMeta } = useSelector(dataMetaSelector(BAND_ACTIONS.getCompanyInvoices));

  const { companyId, companyInvoices } = useCompanyContext();
  const pagination = usePagination(BAND_ACTIONS.getCompanyInvoices);

  const fetchCompanyInvoices = useCallback(
    (page?: number) => {
      if (companyId) {
        dispatchGetCompanyInvoices(dispatch, { company: companyId, page });
      }
    },
    [companyId, dispatch]
  );

  useEffect(() => {
    fetchCompanyInvoices();
  }, [fetchCompanyInvoices]);

  const getRowActions = useCallback(
    (id: string): IActionDropdownItem[] => {
      const invoiceUrl = companyInvoices.find(invoice => invoice.id === id)?.invoiceUrl;

      if (invoiceUrl) {
        return [
          {
            label: invoicesCopy.actions.download,
            handleClick: () => {
              window.open(invoiceUrl, "_blank");
            },
            dataTestId: "download_invoice_action",
          },
        ];
      }

      return [];
    },
    [companyInvoices]
  );

  if (apiMeta?.status === IAPIState.pending) return <TableSkeleton theme={ITheme.tertiary} />;

  return (
    <Spacer size="s">
      <Table
        columns={columns}
        data={companyInvoices}
        emptyCopy={invoicesCopy.empty}
        getRowActions={getRowActions}
      />
      <Pager {...pagination} onPageChange={fetchCompanyInvoices} />
    </Spacer>
  );
};

const columns: TableColumn[] = [
  {
    Header: invoicesCopy.columns.band,
    accessor: "subscription.bands[0].title",
  },
  {
    Header: invoicesCopy.columns.date,
    accessor: "date",
    Cell: ({ value }) => formatDateTime(value, { dateOnly: true }),
  },
  {
    Header: invoicesCopy.columns.amount,
    accessor: "amount",
    Cell: ({ value }) =>
      `€ ${formatNumber(value, {
        minimumFractionDigits: 2,
      })}`,
  },
  {
    Header: invoicesCopy.columns.invoiceId,
    accessor: "invoiceNumber",
  },
  {
    Header: invoicesCopy.columns.payment,
    accessor: "paymentMethod",
  },
];
